/**
 * Home (SPEC §3.2, §7.3): an infinite, virtualised scroll of days, today on top, then every day
 * with content. Today always ends with a "Write something…" ghost line: typing there creates the
 * day and the block. Days load 30 at a time as you scroll.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { hasLiveChildren, journalId, visibleRows } from '@solar/core'
import { ghostForDay, type ViewRow } from '../client/store.ts'
import { todayKey } from './dates.ts'
import { useStore, useStructure } from './hooks.ts'
import { Outline } from './Outline.tsx'
import { navigate, replaceQuietly, useNavSeq } from './router.ts'
import { TaskStrip } from './TaskStrip.tsx'

const PAGE = 30

export function Journal({ date, block }: { date: string | null; block: string | null }) {
  const store = useStore()
  const version = useStructure()
  const navSeq = useNavSeq()
  const today = todayKey()
  const [count, setCount] = useState(PAGE)

  const rows = useMemo(() => {
    const g = store.g
    const days = store.journal.days().filter((d) => d.date !== today && d.date !== date)
    const list = [{ date: today, id: journalId(today) }, ...days]
    // A day you jump to (calendar, ⌘K "Sep 15") is always there, empty or not, to write in.
    if (date && date !== today) {
      const at = list.findIndex((x, i) => i > 0 && x.date < date)
      list.splice(at < 0 ? list.length : at, 0, { date, id: journalId(date) })
    }
    const out: ViewRow[] = []
    for (const d of list.slice(0, Math.max(count, date ? list.findIndex((x) => x.date === date) + 5 : 0))) {
      out.push({ type: 'day', key: `day:${d.date}`, date: d.date })
      if (d.date === today) out.push({ type: 'extra', key: 'strip', what: 'strip', id: today })
      const live = g.isLive(d.id) && hasLiveChildren(g, d.id)
      if (live) for (const r of visibleRows(g, d.id)) out.push({ type: 'block', key: r.id, id: r.id, depth: r.depth, root: d.id })
      if (d.date === today || !live) out.push({ type: 'ghost', key: ghostForDay(d.date), id: ghostForDay(d.date), root: d.id, date: d.date })
    }
    return out
  }, [store, version, today, count, date]) // eslint-disable-line react-hooks/exhaustive-deps

  const total = store.journal.days().length + 1
  const more = useCallback(() => setCount((c) => (c < total ? c + PAGE : c)), [total])
  const highlight = useMemo(() => (block ? { id: block, seq: navSeq } : null), [block, navSeq])

  // The address follows the day in view (SPEC §7.3), so Back and a reload return to it. Only when
  // the day changes, and at most every 300 ms (browsers throttle history updates).
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const shown = useRef<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onTopRow = useCallback(
    (i: number) => {
      const r = rowsRef.current
      let day: string | null = null
      for (let k = Math.min(i, r.length - 1); k >= 0; k--) {
        const x = r[k]!
        if (x.type === 'day') {
          day = x.date
          break
        }
      }
      if (day === null || day === shown.current) return
      shown.current = day
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => replaceQuietly(shown.current === today ? '/' : `/d/${shown.current}`), 300)
    },
    [today],
  )
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), [])

  // Desktop: opening the journal puts the caret on today's empty line (SPEC §3.3: jot = 0 actions).
  useEffect(() => {
    if (date || store.mode.kind !== 'idle' || matchMedia('(pointer: coarse)').matches) return
    store.edit(ghostForDay(today), journalId(today))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Outline
      rows={rows}
      onOpenDay={(d) => navigate(`/d/${d}`)}
      onNearEnd={more}
      scrollToKey={date && !block ? `day:${date}` : null}
      highlight={highlight}
      onTopRow={onTopRow}
      renderExtra={(r) => (r.what === 'strip' ? <TaskStrip /> : null)}
    />
  )
}
